import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import './App.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MailingListSignup from '../components/MailingListSignup';
import HeroFilters from '../components/HeroFilters';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAnalytics } from '../context/AnalyticsContext';
import SimilarCamps from '../components/camps/SimilarCamps';
import { FilterInfo, Camp } from '../utils/types';
import { FilterOptionsUtils } from '../utils/filterOptionsUtils';
import {
  getWinterBreak,
  getMartinLutherKingDayWeekend,
  getPresidentsDayWeekend,
  getSpringBreak
} from '../utils/holidayUtils';

const App: React.FC = () => {

  const navigate = useNavigate();
  const { trackPageView, trackEvent } = useAnalytics();
  const { hash } = useLocation();
  const mailingListRef = useRef<HTMLDivElement>(null);

  const springBreakRange = getSpringBreak(new Date(2026, 0, 1));
  const springBreakFilter: FilterInfo = {
    start_date: springBreakRange.startDate.toISOString().split('T')[0],
    end_date: springBreakRange.endDate.toISOString().split('T')[0]
  };


  

  useEffect(() => {
    if (hash === "#mailing-list-signup" && mailingListRef.current) {
      // Wait for page content to fully load before scrolling
      const timeoutId = setTimeout(() => {
        if (mailingListRef.current) {
          mailingListRef.current.scrollIntoView({ 
            behavior: "smooth", 
            block: "start" 
          });
        }
      }, 500); // Small delay to ensure content is rendered

      return () => clearTimeout(timeoutId);
    }
  }, [hash]);

  useEffect(() => {
    trackPageView('/', 'Landing Page');
  }, [trackPageView]);

  const handleCampClick = (camp: Camp) => {
    trackEvent('landing_page_camp_clicked', { camp_id: camp.id });
    const searchParams = new URLSearchParams();

    searchParams.set('selected_camp', camp.id);    

    navigate(`/search?${searchParams.toString()}`);
  };

  const handleViewAllClick = () => {
    trackEvent('landing_page_similar_camps_view_all_clicked', {});
  };


  const handleMailingListScroll = (e: React.MouseEvent) => {
    e.preventDefault();
    if (mailingListRef.current) {
      mailingListRef.current.scrollIntoView({ 
        behavior: "smooth", 
        block: "start" 
      });
    }
  };

  return (
    <div className="app">
      <Header />

      <main className="landing-main">
        {/* Hero Section */}
        <section className="hero hero-landing">
          <div className="hero-content">
            <h1 className="hero-title">Find the Perfect Camps for Your Child</h1>
            <HeroFilters onMailingListScroll={handleMailingListScroll} />
          </div>
        </section>      

          <section className="landing-similar-camps">
            <div className="landing-similar-camps-content">
              <SimilarCamps 
                filters={springBreakFilter} 
                title="Spring break camps" 
                displayDates={false} 
                displayOrganizer={true} 
                displayRating={true} 
                onCampClick={handleCampClick} 
                oneCampPerOrganizer={true}
                showViewAll={true}
                onViewAllClick={handleViewAllClick}
              />
            </div>
          </section>

        <section className="features">
          <div className="features-two-column">
            <div className="features-column" ref={mailingListRef}>
              <MailingListSignup />
            </div>
            <div className="features-column">
              <h2 className="features-title">Why Aika?</h2>
              <div className="feature-card">
                <div className="feature-icon">⚡</div>
                <h3>Save Time</h3>
                <p>One place to find and compare camps with comprehensive listings from trusted providers.</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">🤝</div>
                <h3>Plan Together</h3>
                <p>Easy collaboration with other parents to find the perfect camps for your kids.</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default App;
